import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.filter.js";
import "core-js/modules/es.iterator.find.js";
import "core-js/modules/es.iterator.some.js";
import { message } from 'ant-design-vue';
import { OK } from '@/common';
import { addEnterpriseAccount, deleteEnterpriseAccount, getEnterpriseAccountList } from '@/api/lawyerBoard';
/**
 * 企业账号列表：加载、前端搜索、选中切换、新增与软删除。
 * 搜索为前端过滤，源列表与展示列表分开，保证清空搜索后可恢复全部有效账号。
 */
export function useAccountList() {
  /** 源列表：接口返回的全部有效账号 */
  const accounts = ref([]);
  const keyword = ref('');
  const loading = ref(false);
  const selectedAccountId = ref('');
  /** 展示列表：按关键字模糊、不区分大小写匹配展示名、登录名与 SSO 用户 ID */
  const displayAccounts = computed(() => {
    const key = keyword.value.trim().toLowerCase();
    if (!key) return accounts.value;
    return accounts.value.filter(item => item.accountName.toLowerCase().includes(key) || item.userName.toLowerCase().includes(key) || item.ssoUserId.toLowerCase().includes(key));
  });
  const selectedAccount = computed(() => accounts.value.find(item => item.accountId === selectedAccountId.value));
  const selectAccount = accountId => {
    if (selectedAccountId.value === accountId) return;
    selectedAccountId.value = accountId;
  };
  /**
   * 加载有效账号列表。
   * @param preferAccountId 期望选中的账号；不传或已失效时默认选中第一条有效账号
   */
  const load = async preferAccountId => {
    loading.value = true;
    try {
      const res = await getEnterpriseAccountList();
      if (res && res.code === OK) {
        var _accounts$value$;
        accounts.value = res.data;
        const expected = preferAccountId || selectedAccountId.value;
        const stillValid = accounts.value.some(item => item.accountId === expected);
        selectedAccountId.value = stillValid ? expected : ((_accounts$value$ = accounts.value[0]) === null || _accounts$value$ === void 0 ? void 0 : _accounts$value$.accountId) || '';
      }
    } catch (error) {
      message.error('企业账号查询失败');
    } finally {
      loading.value = false;
    }
  };
  /** 新增账号；曾被归档的账号会连同历史产品配置一起恢复 */
  const addAccount = async user => {
    const res = await addEnterpriseAccount(user);
    if (!res || res.code !== OK) return false;
    // 新增成功后清空搜索，避免新账号被当前关键字过滤掉而「看起来没添加上」
    keyword.value = '';
    await load(res.data.accountId);
    return true;
  };
  /** 归档账号；删除当前选中账号时自动选中相邻有效账号 */
  const removeAccount = async accountId => {
    const list = displayAccounts.value;
    const index = list.findIndex(item => item.accountId === accountId);
    // 优先取后一条，无则取前一条
    const neighbor = list[index + 1] || list[index - 1];
    const shouldMoveSelection = selectedAccountId.value === accountId;
    const res = await deleteEnterpriseAccount({
      accountId
    });
    if (!res || res.code !== OK) return false;
    if (shouldMoveSelection) {
      selectedAccountId.value = '';
      await load(neighbor === null || neighbor === void 0 ? void 0 : neighbor.accountId);
    } else {
      await load();
    }
    return true;
  };
  /** 重复添加校验：下拉选中的是 SSO 用户，需按 ssoUserId 比对 */
  const isAccountExisted = ssoUserId => accounts.value.some(item => item.ssoUserId === ssoUserId);
  return {
    accounts,
    displayAccounts,
    keyword,
    loading,
    selectedAccountId,
    selectedAccount,
    selectAccount,
    load,
    addAccount,
    removeAccount,
    isAccountExisted
  };
}